import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Property } from "../../../types/search";
import { useTenant } from "../../../contexts/TenantContext";
import { Bed, Bath, Ruler, Heart, Loader } from "lucide-react";
import { Score } from "../../common/Score";
import {
  useAuthUser,
  useIsAuthenticated,
  useHasMissingContact,
} from "@/hooks/auth/useAuth";
import { useNavigate } from "react-router-dom";
import { savedPropertyService } from "../../../api/savedProperties";
import { SqueezePageOverlay } from "../../auth/SqueezePageOverlay";
import { firebaseService } from "../../../lib/firebase";
import { analyticsService } from "../../../services/analyticsService";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import placeholderImage from "/assets/images/place-holder-property-card.webp";
import noPhotosImage from "/assets/images/no-photos-property-card.webp";
import { formatCamelCase } from "../../../utils/formatText";
import { getOptimizedImageUrl } from "../../../utils/imageUtils";

interface PropertyCardProps {
  property: Property;
  onClick?: (property: Property) => void;
  showScore?: boolean;
  hideGoogleSignIn?: boolean;
  isFirstCard?: boolean; // Add this to identify the first card for LCP optimization
  index?: number; // Add index to know if it's the very first card (index === 0)
  mlsLogo?: string; // MLS logo URL passed from parent
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onClick,
  showScore = true,
  hideGoogleSignIn,
  isFirstCard = false, // Default to false
  index = -1, // Default to -1 if not provided
  mlsLogo, // MLS logo URL
}) => {
  const navigate = useNavigate();
  const user = useAuthUser();
  const isAuthenticated = useIsAuthenticated();
  const hasMissingContact = useHasMissingContact();
  const { config } = useTenant();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedPropertyId, setSavedPropertyId] = useState<string | null>(null);
  const [pendingSave, setPendingSave] = useState(false);

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Show loading when auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setIsLoading(true);
    }
  }, [isAuthenticated, user]);

  if (!config) return null;

  // Check if property is already saved on mount and handle auth changes
  useEffect(() => {
    const checkIfSaved = async () => {
      if (!isAuthenticated || !user || !config?.id) return;

      // Show loading while checking saved status
      setIsLoading(true);

      try {
        const savedProperty = await savedPropertyService.findSavedProperty(
          user.id,
          config.id,
          property.mlsNumber
        );
        if (savedProperty) {
          setIsSaved(true);
          setSavedPropertyId(savedProperty.id);
        } else if (pendingSave) {
          // If we just logged in and we have a pending save, try to save

          handleSave();
        }
      } catch (error) {
        console.error("Error checking saved status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkIfSaved();
  }, [isAuthenticated, user, property.mlsNumber, config?.id]);

  const [showSqueezeOverlay, setShowSqueezeOverlay] = useState(false);

  const handleSave = async (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent triggering the card click
      e.preventDefault(); // Prevent right-click menu
    }

    // If user is not authenticated, show login/signup overlay
    if (!isAuthenticated || !user) {
      // Only show squeeze overlay if user is not authenticated
      if (!isAuthenticated) {
        setShowSqueezeOverlay(true);
        setPendingSave(true);
        return;
      }
      return;
    }

    if (!config?.id) {
      console.error("Organization ID not found in config");
      return;
    }

    setIsLoading(true);
    try {
      if (!user.id) {
        throw new Error("Contact ID not found");
      }

      if (isSaved && savedPropertyId) {
        // Unsave the property
        const result = await savedPropertyService.removeSavedProperty(
          savedPropertyId
        );
        if (result.success) {
          setIsSaved(false);
          setSavedPropertyId(null);
          queryClient.invalidateQueries({ queryKey: ["savedProperties"] });
        }
      } else {
        const result = await savedPropertyService.saveProperty(
          user.id,
          config.id,
          property.id,
          property.mlsNumber,
          property.mlsBoardCode,
          {
            full: property.address.full || "",
            city: property.address.city || "",
            state: property.address.state || "",
            zip: property.address.zipcode || "",
          },
          {
            bedrooms: property.bedrooms || 0,
            bathrooms: property.bathrooms || 0,
            squareFeet: property.squareFeet || property.lotSizeSquareFeet || 0,
            price: property.price || 0,
            listprice: property.property?.listprice,
            listpricelow: property.property?.listpricelow,
            internetAddressDisplayYN: property.internetAddressDisplayYN,
            mustRemovePrimaryPhoto:
              property.displayRules?.mustRemovePrimaryPhoto,
            primaryPhotoUrl:
              property.images && property.images.length > 0
                ? property.images[0]
                : placeholderImage,
            features:
              property.features
                ?.flat(Infinity)
                .filter((f) => f && typeof f === "string")
                .map(String) || [],
            listingStatus: property.listingStatus || "Active",
            listingOffice: property.listingOffice?.name || "-",
            mlsstatus: property.mlsstatus,
          }
        );

        if (result.success && result.property) {
          setIsSaved(true);
          setSavedPropertyId(result.property.id);
          queryClient.invalidateQueries({ queryKey: ["savedProperties"] });

          // Track property save event
          analyticsService.trackEvent({
            tenantId: config?.id,
            orgId: config?.orgData?.id,
            eventType: "interaction",
            category: "Property",
            action: "Save Property",
            label:
              property.address.full ||
              `${property.address.city}, ${property.address.state}`,
            data: {
              propertyId: property.id,
              mlsNumber: property.mlsNumber,
              mlsBoardCode: property.mlsBoardCode,
              address: property.address.full || "",
              city: property.address.city || "",
              state: property.address.state || "",
              zipCode: property.address.zipcode || "",
              bedrooms: property.bedrooms,
              bathrooms: property.bathrooms,
              squareFeet: property.squareFeet || property.lotSizeSquareFeet,
              price: property.price,
            },
          });
        }
      }
    } catch (error) {
      console.error("Error handling property save/unsave:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick(property);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const tenantId = config?.id || "";
    const mlsOsn = property.mlsBoardCode || "";
    window.open(
      `/property/${property.id}?tenant_id=${tenantId}&mls_osn=${mlsOsn}`,
      "_blank"
    );

    //>>>> TEMP ORIG: window.open(`/property/${property.id}`, "_blank");
  };
  return (
    <>
      <div
        className="rounded-xl shadow-md overflow-hidden cursor-pointer transform transition-all hover:scale-[1.02] bg-white"
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        <div className="relative">
          {isFirstCard ? (
            // First card - load eagerly with high priority for LCP optimization
            <img
              src={
                property.displayRules?.mustRemovePrimaryPhoto === true
                  ? noPhotosImage
                  : property.images && property.images.length > 0
                  ? getOptimizedImageUrl(
                      property.images[0],
                      isMobile ? 200 : 600 // Reduced from 348 to 200 for faster mobile LCP
                    )
                  : placeholderImage
              }
              alt={property.address.full}
              className="object-cover w-full h-[180px] lg:h-[200px]"
              loading="eager"
              fetchPriority={index === 0 && isFirstCard ? "high" : "auto"} // Only the very first card gets high priority for LCP
              decoding="async" // Changed to async for better performance
            />
          ) : (
            // Other cards - use data-src pattern for true lazy loading
            <img
              src={placeholderImage} // Start with placeholder
              data-src={
                property.displayRules?.mustRemovePrimaryPhoto === true
                  ? noPhotosImage
                  : property.images && property.images.length > 0
                  ? getOptimizedImageUrl(
                      property.images[0],
                      isMobile ? 348 : 600
                    )
                  : placeholderImage
              }
              alt={property.address.full}
              className="object-cover w-full h-[180px] lg:h-[200px] lazyload"
              loading="lazy"
              onLoad={(e) => {
                const img = e.currentTarget;

                // Only set up lazy loading once - check if already processed
                if (img.dataset.lazyLoaded) return;

                const observer = new IntersectionObserver(
                  (entries) => {
                    if (entries[0].isIntersecting) {
                      const dataSrc = img.getAttribute("data-src");
                      // Only replace if we haven't already loaded the actual image
                      if (dataSrc && img.src !== dataSrc) {
                        img.src = dataSrc;
                        img.dataset.lazyLoaded = "true"; // Mark as loaded to prevent re-processing
                      }
                      observer.disconnect();
                    }
                  },
                  {
                    rootMargin: "200px", // Load when within 200px of viewport
                    threshold: 0.01,
                  }
                );

                observer.observe(img);
              }}
            />
          )}
          <div className="absolute inset-0">
            {showScore && property.score !== undefined && (
              <div className="absolute top-3 left-3">
                <Score score={property.score} />
              </div>
            )}
            <button
              onClick={handleSave}
              className="absolute top-3.5 right-3 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-md"
              style={{
                color: isSaved ? config.branding.colors.primary : "#666666",
              }}
            >
              {isLoading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <Heart className={`w-5 h-5 ${isSaved ? "fill-current" : ""}`} />
              )}
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="space-y-1 lg:space-y-2">
            {/* Price and Status */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-black !font-bold text-lg lg:text-xl mr-2">
                {(() => {
                  // RMLS Compliance for PortlandORSpeculo
                  if (property.mlsBoardCode === "PortlandORSpeculo") {
                    const listprice =
                      property.listprice || property.property?.listprice;
                    const listpricelow =
                      property.listpricelow || property.property?.listpricelow;

                    // If both exist and differ and neither is 0, show range
                    if (
                      listprice &&
                      listpricelow &&
                      parseFloat(listprice) !== parseFloat(listpricelow) &&
                      parseFloat(listpricelow) > 0
                    ) {
                      return `${formatPrice(
                        parseFloat(listpricelow)
                      )} - ${formatPrice(parseFloat(listprice))}`;
                    }

                    // Otherwise show single price
                    const priceToDisplay = listprice
                      ? parseFloat(listprice)
                      : property.price;
                    return priceToDisplay ? formatPrice(priceToDisplay) : "-";
                  }

                  // For other MLS boards, use regular price
                  return formatPrice(property.price);
                })()}
              </div>
              <span
                className="px-2 py-1 rounded-full text-white text-[11px] lg:text-xs"
                style={{
                  backgroundColor: (() => {
                    const status = property.listingStatus?.toLowerCase();
                    if (
                      status === "active" ||
                      status === "active under contract"
                    )
                      return "#16a34a";
                    if (status === "pending") return "#E5A73B"; // Darker yellow
                    if (status === "closed") return "#ef4444";
                    return "#969696";
                  })(),
                }}
              >
                {(() => {
                  const standardStatus = property.listingStatus || "Active";
                  const mlsStatus = property.mlsstatus;
                  const mlsBoardCode = property.mlsBoardCode;

                  // Format mlsStatus to add spaces (e.g., "BumpableBuyer" -> "Bumpable Buyer")
                  const formattedMlsStatus = mlsStatus
                    ? formatCamelCase(mlsStatus)
                    : "";

                  // RMLS Compliance: If PortlandORSpeculo and status is not Active, only show mlsStatus
                  if (
                    mlsBoardCode === "PortlandORSpeculo" &&
                    standardStatus.toLowerCase() !== "active"
                  ) {
                    return formattedMlsStatus || standardStatus;
                  }

                  // If both exist and are different, show "standardStatus - mlsStatus"
                  if (
                    mlsStatus &&
                    typeof mlsStatus === "string" &&
                    standardStatus.toLowerCase() !== mlsStatus.toLowerCase()
                  ) {
                    return `${standardStatus} - ${formattedMlsStatus}`;
                  }

                  // Otherwise just show mlsStatus if it exists, or standardStatus
                  return formattedMlsStatus || standardStatus;
                })()}
              </span>

              {/* Special Property Indicators next to status */}
              {property.property?.isNewConstruction && (
                <span className="px-2 py-1 text-[11px] lg:text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  New Construction
                </span>
              )}
              {(property.property?.isRental ||
                property.propertyType === "Residential Lease") && (
                <span className="px-2 py-1 text-[11px] lg:text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                  Rental
                </span>
              )}
            </div>
            {/* Address */}
            <p className="!font-semibold text-gray-900 line-clamp-1 text-sm lg:text-[17px]">
              {property.internetAddressDisplayYN === false
                ? `${property.address.city}, ${property.address.state} ${property.address.zipcode}`
                : property.address.full}
            </p>

            {/* Property Details */}
            <div className="flex items-center gap-4 text-xs lg:text-sm text-gray-700">
              {property.propertyType === "Land Lot" ||
              property.propertyType === "Land" ||
              property.propertyType === "Lot" ||
              property.propertyType === "Farm" ||
              property.propertyType === "Retail" ||
              property.propertyType === "Office" ||
              property.propertyType === "Industrial" ||
              property.propertyType === "Commercial Lease" ||
              property.propertyType === "Commercial Sale" ||
              property.propertyType === "Business Opportunity" ? (
                property.lotSizeSquareFeet || property.squareFeet ? (
                  <div className="flex items-center gap-1.5">
                    <div className="p-1">
                      <Ruler
                        className="w-4 h-4"
                        style={{ color: config.branding.colors.primary }}
                      />
                    </div>
                    <span className="font-medium">
                      {property.lotSizeSquareFeet?.toLocaleString() ||
                        property.squareFeet?.toLocaleString()}
                    </span>
                    <span className="text-gray-500">sf lot</span>
                  </div>
                ) : null
              ) : (
                <>
                  {property.bedrooms != null && property.bedrooms > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="p-1">
                        <Bed
                          className="w-4 h-4"
                          style={{ color: config.branding.colors.primary }}
                        />
                      </div>
                      <span className="font-medium">{property.bedrooms}</span>
                    </div>
                  )}
                  {property.bathrooms != null && property.bathrooms > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="p-1">
                        <Bath
                          className="w-4 h-4"
                          style={{ color: config.branding.colors.primary }}
                        />
                      </div>
                      <span className="font-medium">{property.bathrooms}</span>
                    </div>
                  )}
                  {property.squareFeet != null && property.squareFeet > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="p-1">
                        <Ruler
                          className="w-4 h-4"
                          style={{ color: config.branding.colors.primary }}
                        />
                      </div>
                      <span className="font-medium">
                        {property.squareFeet.toLocaleString()}
                      </span>
                      <span className="text-gray-500">sf</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Features */}
            {property.features && property.features.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2 text-xs lg:text-sm">
                {(() => {
                  // Filter out "New Construction" and "Rental" from features since we're showing them separately
                  const validFeatures = property.features
                    .map((feature) => (feature ? String(feature) : ""))
                    .filter(
                      (feature) =>
                        feature.trim() !== "" &&
                        feature !== "New Construction" &&
                        feature !== "Rental"
                    );
                  return validFeatures.length > 0 ? (
                    <>
                      {validFeatures.slice(0, 3).map((feature, index) => (
                        <span
                          key={index}
                          className="px-2 py-0.5 text-xs bg-gray-200 text-gray-800 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                      {validFeatures.length > 3 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-800 rounded-full">
                          +{validFeatures.length - 3} more
                        </span>
                      )}
                    </>
                  ) : null;
                })()}
              </div>
            )}

            {/* MLS Info */}
            <div className="pt-2 text-[11px] lg:text-xs text-gray-500">
              <hr className="mb-3"></hr>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p>MLS# {property.mlsNumber}</p>
                  <p>
                    Listing Courtesy of: {property.listingOffice?.name || "-"}
                  </p>
                </div>
                {mlsLogo && (
                  <img
                    src={mlsLogo}
                    alt={`${property.mlsBoardCode} logo`}
                    className="h-5  object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Squeeze Page Overlay */}
      {/* Squeeze Page Overlay - Only show for non-authenticated users */}
      {showSqueezeOverlay && !isAuthenticated && (
        <SqueezePageOverlay
          onClose={() => setShowSqueezeOverlay(false)}
          onSuccess={() => {
            console.log("[PropertyCard] Closing overlay");
            setShowSqueezeOverlay(false);
            // Keep pendingSave true so useEffect can handle the save
          }}
          propertyId={property.id}
          closeOnlyModal={true}
          hideGoogleSignIn={hideGoogleSignIn}
          hasMissingContact={hasMissingContact}
        />
      )}
    </>
  );
};

export default PropertyCard;
